import useMe from '@hooks/useMe';

/**
 * useOnboardingActive
 *
 * Single source of truth for whether onboarding entry points (tooltips, nudges,
 * coachmarks) are allowed to render. The server computes `onboardingComplete` on
 * the `getMe` query (TA-10033). We show onboarding UI ONLY when it is explicitly
 * `false`:
 *  - `undefined` (getMe still loading / not yet resolved) → hidden, to avoid a
 *    flash of onboarding UI before the query resolves.
 *  - `true` (user finished all steps, or a legacy user with blank onboarding
 *    flags) → hidden; legacy users are treated as a skip signal.
 *
 * We also gate on `meNetworkReady`: getMe runs cache-and-network, so a stale
 * persisted snapshot with `onboardingComplete: false` can render before the
 * server-confirmed response arrives. A completed/legacy user whose cache holds
 * an old `false` would otherwise flash onboarding UI until the network result
 * (`true`) lands. Holding the gate until the network confirms the value keeps
 * the no-flash acceptance criterion intact. See useMe (meNetworkReady) and
 * useHashtagTooltip for the same stale-false→network-true pattern.
 *
 * Gate every onboarding tooltip/nudge visibility flag with this value.
 * See TA-10039 (epics TA-9668 web / TA-9780 mobile).
 */
export const useOnboardingActive = (): boolean => {
  const { me, meNetworkReady } = useMe();
  return meNetworkReady && me?.onboardingComplete === false;
};

/**
 * useOnboardingFinished
 *
 * Inverse gate for post-onboarding surfaces (the numbered Smart Nudges 2A–3E).
 * TA-10920 #19: those nudges must stay hidden while onboarding is in progress
 * and re-evaluate once it completes. Like useOnboardingActive it waits for the
 * network-confirmed getMe, so a stale cached `true` can't flash a nudge at a
 * user who is mid-onboarding. `undefined` after the network resolves means a
 * legacy user without onboarding data, which counts as finished.
 */
export const useOnboardingFinished = (): boolean => {
  const { me, meNetworkReady } = useMe();
  return meNetworkReady && !!me && me.onboardingComplete !== false;
};
